import React, { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Formik, FormikProps } from 'formik';
import { isUndefined } from 'lodash';
import { addAdditionalBreadcrumbParams } from 'Store';
import useGetUngroupedTenants from 'Hooks/query-hooks/tenant/useGetUngroupedTenants';
import useAddTenantsToTenantGroup from 'Hooks/query-hooks/tenant-groups/useAddTenantsToTenantGroup';
import useGetTenantGroup from 'Hooks/query-hooks/tenant-groups/useGetTenantGroup';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import AddTenantPageHeader from './components/AddTenantPageHeader';
import TenantCheckboxTable from '../../components/TenantCheckboxTable';

import styles from './AddTenant.css';

const AddTenant = () => {
    const { tenantGroupId } = useParams<{ tenantGroupId: string }>();
    const ungroupedTenantsAPI = useGetUngroupedTenants();
    const tenantGroupAPI = useGetTenantGroup({
        tenantGroupId
    });
    const { hasError, errorCmp } = errorController([ungroupedTenantsAPI.error, tenantGroupAPI.error]);
    const formRef = useRef<FormikProps<TenantCheckboxTableForm>>(null);
    const dispatch = useDispatch();
    const location = useLocation();

    useEffect(() => {
        if (tenantGroupAPI.data) {
            dispatch(
                addAdditionalBreadcrumbParams({
                    pathname: `/tenants/tenant-group/${tenantGroupId}`,
                    params: {
                        tenantGroupName: tenantGroupAPI.data?.name
                    }
                })
            );
        }
    }, [location, tenantGroupId, tenantGroupAPI.data, dispatch]);

    const mutation = useAddTenantsToTenantGroup({
        formRef,
        tenantGroupId,
        tenantGroupName: tenantGroupAPI.data?.name
    });

    const handleSubmit = (formValues: TenantCheckboxTableForm) => {
        const tenantIds = formValues.rows.filter((r) => r.isSelected).map((r) => r.tenant);
        mutation.mutate({
            tenantKeys: tenantIds
        });
    };

    if (hasError || isUndefined(ungroupedTenantsAPI.data?.modifiedTenants)) {
        return <>{errorCmp}</>;
    }

    if (
        ungroupedTenantsAPI.isLoading ||
        ungroupedTenantsAPI.isFetching ||
        tenantGroupAPI.isLoading ||
        tenantGroupAPI.isFetching
    ) {
        return <RouteLoading />;
    }

    const tableInitialState = {
        rows: ungroupedTenantsAPI.data?.modifiedTenants ?? [],
        headerCheckboxSelected: false
    };

    return (
        <div className={styles.pageContainer}>
            <Formik enableReinitialize initialValues={tableInitialState} onSubmit={handleSubmit} innerRef={formRef}>
                <>
                    <AddTenantPageHeader tenantGroupId={tenantGroupId} mutation={mutation} />

                    <div className={styles.tableContainer}>
                        <TenantCheckboxTable tableName='addTenantTable' />
                    </div>
                </>
            </Formik>
        </div>
    );
};

export default AddTenant;
